import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {
  FieldTree,
  FormField,
  type MaxLengthValidationError,
  type MinLengthValidationError,
} from '@angular/forms/signals';
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
  selector: 'app-text-input',
  imports: [FormField, SvgIconComponent],
  templateUrl: './text-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Keeps a caller's static `id` attribute off the host element, which would
  // otherwise duplicate the `id` the template below puts on the real `<input>`.
  host: { '[attr.id]': 'null' },
})
export class TextInputComponent {
  readonly field = input.required<FieldTree<string>>();
  readonly id = input('');
  readonly label = input('');
  readonly placeholder = input('');
  readonly type = input<'text' | 'email' | 'password' | 'number'>('text');
  readonly autocomplete = input('');
  readonly prefixIcon = input('');
  readonly hideLabel = input(false);
  readonly containerClass = input('');
  readonly inputClass = input('');
  readonly maxLength = input<number | null>(null);
  readonly errorMessages = input<Record<string, string>>({});
  readonly testId = input('');

  private readonly inputRef =
    viewChild<ElementRef<HTMLInputElement>>('inputRef');

  protected readonly passwordVisible = signal(false);

  protected readonly state = computed(() => this.field()());

  protected readonly labelText = computed(() =>
    this.state().required() ? `${this.label()} *` : this.label()
  );

  protected readonly effectiveContainerClass = computed(
    () => this.containerClass() || 'flex flex-col gap-1.5'
  );

  protected readonly effectiveType = computed(() =>
    this.type() === 'password' && this.passwordVisible() ? 'text' : this.type()
  );

  protected readonly showError = computed(() => {
    const state = this.state();
    return state.touched() && state.errors().length > 0;
  });

  protected readonly errorMessage = computed(() => {
    const error = this.state().errors()[0];
    if (!error) {
      return '';
    }

    const override = this.errorMessages()[error.kind];
    if (override) {
      return override;
    }

    if (error.message) {
      return error.message;
    }

    switch (error.kind) {
      case 'required':
        return 'This is a required field';
      case 'minLength':
        return `Minimum ${(error as MinLengthValidationError).minLength} characters required`;
      case 'maxLength':
        return `Maximum ${(error as MaxLengthValidationError).maxLength} characters allowed`;
      case 'pattern':
        return 'Invalid format';
      default:
        return 'This field is invalid';
    }
  });

  constructor() {
    // `[formField]` owns the native `maxlength` attribute, so a template binding to it is
    // NG8022 — this per-instance limit is set on the element directly.
    effect(() => {
      const el = this.inputRef()?.nativeElement;
      if (!el) {
        return;
      }
      const max = this.maxLength();
      if (max === null) {
        el.removeAttribute('maxlength');
      } else {
        el.setAttribute('maxlength', String(max));
      }
    });
  }

  protected togglePasswordVisibility(): void {
    this.passwordVisible.update(visible => !visible);
  }
}
